import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProjects } from '../../lib/api/projects';
import type { AnalyticsRange } from '../../../../shared/types/analytics';

const RANGES: { key: AnalyticsRange; label: string }[] = [
  { key: '7d', label: 'last 7d' },
  { key: '30d', label: 'last 30d' },
  { key: '90d', label: 'last 90d' },
  { key: 'all', label: 'all time' },
];

/**
 * UX-02 — the hub's one shared filter bar (range + project). Lives in the
 * page header; every tab reads its values from `useInsightsFilters()`
 * instead of rendering its own range selector.
 */
export const InsightsFilterBar: React.FC<{
  since: AnalyticsRange;
  setSince: (s: AnalyticsRange) => void;
  project: string | undefined;
  setProject: (p: string | undefined) => void;
}> = ({ since, setSince, project, setProject }) => {
  const projectsQ = useQuery({
    queryKey: ['projects', 'insights-filter-bar'],
    queryFn: () => fetchProjects({ limit: 200 }),
    staleTime: 60_000,
  });
  const projects = projectsQ.data?.data.projects ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="insights-filter-bar">
      <div
        className="inline-flex bg-white border border-slate-200 rounded-lg shadow-card p-0.5"
        role="radiogroup"
        aria-label="Time range"
      >
        {RANGES.map((r) => (
          <button
            key={r.key}
            role="radio"
            aria-checked={since === r.key}
            type="button"
            onClick={() => setSince(r.key)}
            data-testid={`range-${r.key}`}
            className={
              'px-3 py-1.5 text-xs rounded-md font-medium transition-colors ' +
              (since === r.key
                ? 'bg-primary-50 text-primary-700'
                : 'text-slate-600 hover:bg-slate-50')
            }
          >
            {r.label}
          </button>
        ))}
      </div>
      <select
        value={project ?? ''}
        onChange={(e) => setProject(e.target.value || undefined)}
        aria-label="Project"
        data-testid="insights-project-select"
        className="border border-slate-300 rounded-md text-xs px-2 py-1.5 bg-white"
      >
        <option value="">All projects</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
    </div>
  );
};

export default InsightsFilterBar;
